import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type RefObject,
} from 'react';
import { CellContextMenu } from './CellContextMenu';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import clsx from 'clsx';
import {
  COL_WIDTH_MAX_PX,
  COL_WIDTH_MIN_PX,
  DEFAULT_ZOOM,
  ZOOM_LEVELS,
  type GridCell,
  type SheetLayout,
  type SheetSectionHeader,
  type ZoomLevel,
} from '@arcanum-hq/shared-types';
import type { ModelViewSchemaSubset } from '@/lib/api';
import { buildCellIndex, colToA1, resolveValue } from './grid-helpers';
import {
  formatCellValue,
  formatCellValueFragment,
  getCellClassName,
  getCellErrorAddress,
  getTableInputRegions,
  isErrorValue,
  isMissingValueError,
  isLeftEdgeOfTableRegion,
  type TableInputRegion,
} from './cell-style';
import { PanelDashboard } from './PanelDashboard';

export interface ModelGridProps {
  layout: SheetLayout;
  cellValues: Record<string, unknown>;
  inputValues: Record<string, unknown>;
  schema: ModelViewSchemaSubset;
  selectedCell: { row: number; col: number } | null;
  onCellSelect: (row: number, col: number) => void;
  /**
   * Hides edit affordances when true. Phase 3 has no edit affordances yet —
   * this prop is here so `external-sharing` (a future feature) can adopt the
   * grid without prop changes. Phase 4 wires the actual edit handlers.
   */
  readOnly?: boolean;
  /**
   * Tree-navigator-driven scroll target (Phase 4 Session 1). The parent
   * sets a fresh object (with a bumped `nonce`) on every leaf click so
   * re-clicking the SAME cell re-fires the scroll + pulse — referential
   * equality on `scrollToCell` drives the effect dependency. Pass `null`
   * (or omit) when there's no pending scroll.
   */
  scrollToCell?: { row: number; col: number; nonce: number } | null;
  /**
   * Per-column width override (in CSS pixels). Keys are stringified column
   * indices (matches the JSONB shape persisted via `merge_ui_prefs`).
   * Columns absent from the map render at the default `COL_WIDTH = 96`.
   * Clamped at the API boundary; the renderer trusts whatever lands here.
   * (model-view-ux-polish Phase 3 Session 3.4, 2026-04-27)
   */
  colWidths?: Record<string, number>;
  /**
   * Fires on drag-end with the final width (already clamped client-side
   * to [COL_WIDTH_MIN_PX, COL_WIDTH_MAX_PX]). Parent debounces + persists
   * via the `useUiPrefs` mutation. Optional — without it the drag still
   * updates local visual state but doesn't propagate.
   */
  onColWidthChange?: (colIndex: number, widthPx: number) => void;
  /**
   * Zoom level — DIMENSIONAL multiplier for row height, col width, font size,
   * and header dimensions. DOES NOT use CSS transform: scale() (that would
   * distort the sticky-header geometry). All constants are re-derived per
   * render as `Math.round(BASE * (zoom / 100))`.
   * (model-view-excel-parity Phase 2 S2, ADR-0012)
   * Default: 100 (no scaling).
   */
  zoom?: ZoomLevel;
  /**
   * Phase 2 S3 — closure-staleness mirror for the grid-root keyboard handler.
   * The handler is a long-lived useCallback closure; reading `selectedCell`
   * directly from the closure causes stale reads on rapid arrow nav (200+
   * keypresses/s). The ref is kept in sync via `useEffect [selectedCell]` in
   * the parent (ModelLayout) so the handler always reads the latest value.
   * (research/phase-2.md § 11 R-G; ADR-0008)
   */
  selectedCellRef?: RefObject<{ row: number; col: number } | null>;
  /**
   * Phase 2 S3 — called when the keyboard handler needs to clear selection
   * (Escape key). Separate from `onCellSelect` because that only accepts
   * (row, col), not null. Optional — without it, Escape is a no-op.
   */
  onClearSelection?: () => void;
  /**
   * Phase 2 S4 — conversation id threaded from ModelLayout for the context-menu
   * public-viewer gate. null = public viewer (DealModelView / share route) →
   * "View in conversation" action hidden. undefined treated as null (no-op).
   */
  conversationId?: string | null;
  /**
   * Phase 2 S4 — fires when the user clicks "Reveal in tree" in the context
   * menu. Wired to ModelLayout's `selectAndPulse` callback.
   */
  onReveal?: (target: { sheet: string; row: number; col: number }) => void;
  /**
   * Phase 2 S4 — fires when the user clicks "Show provenance" in the context
   * menu. Wired to a ModelLayout callback that opens CellDetailPanel to the
   * provenance section.
   */
  onShowProvenance?: (target: {
    sheet: string;
    row: number;
    col: number;
  }) => void;
  /**
   * Phase 2 S4 — fires when the user clicks "View in conversation" in the
   * context menu. Optional — callers pass undefined when conversationId is null
   * (public viewer) so the action is inert even if rendered.
   */
  onViewInConversation?: (target: {
    sheet: string;
    row: number;
    col: number;
  }) => void;
  /**
   * Phase 2 S3 — called when Ctrl+Shift+=/-/0 zoom shortcuts fire from the
   * grid-root keyboard handler. Optional — without it the zoom shortcuts are
   * handled by the parent ModelLayout root listener only (they bubble up).
   * Passing this prop lets the grid-root handler intercept + preventDefault
   * before the event reaches the parent.
   */
  onZoomChange?: (zoom: ZoomLevel) => void;
}

// ---------------------------------------------------------------------------
// Sizing constants — `COL_WIDTH` is the per-column DEFAULT (when the
// `colWidths` override map has no entry for that index). `ROW_HEIGHT`,
// `ROW_HEADER_WIDTH`, and `COL_HEADER_HEIGHT` are fixed across the grid.
//
// Variable per-column widths landed in model-view-ux-polish Phase 3
// Session 3.4 (2026-04-27): the `colWidths?: Record<string, number>` prop
// flows into `colVirtualizer.estimateSize` AND `<DataCell>`'s merge-anchor
// `width` calculation (via `sumColWidths(startCol, spanCols, colWidths)`).
// If new affordances ever fold non-default ROW heights or other variable
// dimensions in, they must thread the same lockstep contract through both
// the virtualizer and the merge-span math.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Base sizing constants — DIMENSIONAL zoom (model-view-excel-parity Phase 2 S2,
// ADR-0012). Component-scope zoom-aware values are derived from these bases
// inside ModelGrid using `Math.round(BASE * zoomFactor)`. NEVER pass these
// raw constants to virtualizer / section-brick / sticky-strip consumers —
// always use the zoom-aware component-scope derivations.
// ---------------------------------------------------------------------------
const BASE_ROW_HEIGHT = 22; // 24 → 22 — Lovable ROW_H target (research/phase-1.md § 4 Lovable ROW_H target; § 1 #1)
const BASE_COL_WIDTH = 96; // UNCHANGED — spec §1: col-width unchanged (research/phase-1.md § 3)
const BASE_ROW_HEADER_WIDTH = 30; // 48 → 30 — tight Excel gutter L5 (research/phase-1.md § 1 #3; Ref #3 BuilderPage.tsx)
const BASE_COL_HEADER_HEIGHT = 22; // 28 → 22 — col-header strip parity (research/phase-1.md § 1 #3; § 4 Lovable target)
const BASE_FONT_SIZE = 11; // matches text-[11px] in cell-style.ts base constant

/**
 * `<ModelGrid>` — virtualized 2-axis spreadsheet grid for the in-platform
 * Model tab (in-platform-model-view Phase 3 Session 2; sticky-headers
 * topology refactored in model-view-ux-polish Phase 1 Session 1.2).
 *
 * Architecture:
 *   - Single scroll container (the outer `<div ref={scrollRef}>`) owns both
 *     row + col scrolling. `useVirtualizer × 2` (one per axis) reads the
 *     same scroll element and emits virtual items only for the visible
 *     range + overscan.
 *   - The render tree splits into FOUR layers inside a single sized inner
 *     div, in painting order (later = higher visual stack on overlap):
 *       1. **Data + section headers** — absolute-positioned at virtualRow/Col
 *          offsets. zIndex 0 (data), 1 (section headers + merge anchors),
 *          1 or 2 if also in a freeze pane.
 *       2. **Row-header column** — `position: sticky; left: 0; height:
 *          totalHeight; zIndex: 2`. Pins to scroll container's left during
 *          horizontal scroll; scrolls with content vertically. Children are
 *          absolute-positioned at `top: virtualRow.start`.
 *       3. **Col-header strip** — `position: sticky; top: 0; width:
 *          totalWidth; zIndex: 2`. Pins to scroll container's top during
 *          vertical scroll; scrolls with content horizontally. Children are
 *          absolute-positioned at `left: virtualCol.start`.
 *       4. **Corner cell** — `position: sticky; top: 0; left: 0; zIndex: 3`.
 *          Sibling of the strips (NOT nested) so its sticky axes operate
 *          independently of its parents. Pins both axes simultaneously,
 *          masking the strip overlap at the top-left.
 *   - Z-stacking: corner (3) > headers (2) > section headers / merge anchors
 *     (1) > data (0). Freeze cells get +1 to overlay scrolling siblings;
 *     freeze + merge anchors stack at zIndex 2.
 *   - Merge-anchor cells (`cell.mergeSpan`) render with absolute positioning,
 *     oversized to span the full merged region. Non-anchor merged cells
 *     (`cell.mergedInto`) skip rendering entirely — the anchor covers them.
 *   - Click selection lifts to the parent via `onCellSelect(row, col)`.
 */
/**
 * Sums per-column widths over the merge anchor's spanned columns.
 *
 * Resolution chain (Increment 2, model-view-lovable-density-alignment):
 *   colWidths?.[i] ?? labelColWidths?.get(i) ?? COL_WIDTH
 *
 * The `colWidths` user drag-override wins (Session-3.4 contract + RotateCcw
 * reset invariant). When absent, the content-fit width from `labelColWidths`
 * is used. Falls back to `COL_WIDTH` when neither is set.
 *
 * This lockstep contract ensures brick/band widths always match the virtualizer
 * column widths (both call this function with the same resolution chain).
 */
function sumColWidths(
  startCol: number,
  spanCols: number,
  colWidths?: Record<string, number>,
  labelColWidths?: Map<number, number>,
  defaultColWidth: number = BASE_COL_WIDTH
): number {
  let total = 0;
  for (let i = 0; i < spanCols; i++) {
    const colIndex = startCol + i;
    total +=
      colWidths?.[String(colIndex)] ??
      labelColWidths?.get(colIndex) ??
      defaultColWidth;
  }
  return total;
}

// ---------------------------------------------------------------------------
// Offscreen canvas for measureText — one instance reused across all calls.
// Created at module level so it is not re-created per-render.
// In JSDOM (vitest), `OffscreenCanvas` may be absent and `measureText` may
// return 0 — the `labelColWidths` memo degrades gracefully to `COL_WIDTH` for
// any column where measurement fails or returns ≤ 0.
// ---------------------------------------------------------------------------
let _measureCanvas: OffscreenCanvas | null = null;
let _measureCtx: OffscreenCanvasRenderingContext2D | null = null;

/**
 * Measure the pixel width of `text` at the exact data-cell font.
 * Returns 0 when the canvas API is unavailable (JSDOM / SSR).
 *
 * Font: `11px "JetBrains Mono"` — matches the `text-[11px] font-mono` base
 * class applied to every data cell in `cell-style.ts:297` (`base` constant).
 *
 * Padding: `px-2` = 8px left + 8px right = 16px total.
 * Epsilon: 4px safety buffer so the last character isn't visually clipped.
 * Total extra = 16 + 4 = 20px added to the raw measureText result.
 */
const CELL_FONT = '11px "JetBrains Mono"';
const CELL_H_PADDING = 16; // px-2 = 8px × 2
const CELL_EPSILON = 4; // safety buffer

function measureLabelPx(text: string): number {
  try {
    if (!_measureCanvas) {
      if (typeof OffscreenCanvas === 'undefined') return 0;
      _measureCanvas = new OffscreenCanvas(1, 1);
      _measureCtx = _measureCanvas.getContext('2d');
    }
    if (!_measureCtx) return 0;
    _measureCtx.font = CELL_FONT;
    const { width } = _measureCtx.measureText(text);
    if (!width || !Number.isFinite(width) || width <= 0) return 0;
    return width + CELL_H_PADDING + CELL_EPSILON;
  } catch {
    return 0;
  }
}

function clampWidth(px: number): number {
  if (px < COL_WIDTH_MIN_PX) return COL_WIDTH_MIN_PX;
  if (px > COL_WIDTH_MAX_PX) return COL_WIDTH_MAX_PX;
  return px;
}

export function ModelGrid({
  layout,
  cellValues,
  inputValues,
  schema,
  selectedCell,
  onCellSelect,
  onClearSelection,
  selectedCellRef,
  onZoomChange,
  readOnly,
  scrollToCell,
  colWidths,
  onColWidthChange,
  zoom = 100,
  conversationId = null,
  onReveal,
  onShowProvenance,
  onViewInConversation,
}: ModelGridProps) {
  // ---------------------------------------------------------------------------
  // Phase 2 S2 DIMENSIONAL zoom — single-source derivation (ADR-0012).
  // ALL virtualizer / section-brick / sticky-strip consumers read from here.
  // NEVER pass BASE_* constants directly to any layout consumer below.
  // `Math.round` avoids sub-pixel shimmer; 6 zoom levels all map to integers
  // or round cleanly (22 × 0.75 = 16.5 → 17, etc.).
  // ---------------------------------------------------------------------------
  const zoomFactor = zoom / 100;
  const ROW_HEIGHT = Math.round(BASE_ROW_HEIGHT * zoomFactor);
  const COL_WIDTH = Math.round(BASE_COL_WIDTH * zoomFactor);
  const ROW_HEADER_WIDTH = Math.round(BASE_ROW_HEADER_WIDTH * zoomFactor);
  const COL_HEADER_HEIGHT = Math.round(BASE_COL_HEADER_HEIGHT * zoomFactor);
  const FONT_SIZE = Math.round(BASE_FONT_SIZE * zoomFactor);

  const scrollRef = useRef<HTMLDivElement>(null);
  // Tree-navigator pulse target — `{row, col}` while pulsing, `null` after
  // the 2s timer expires (cleared by the effect below). Drives the
  // `animate-pulse-cell` class on the matching `<DataCell>`.
  const [pulsedCell, setPulsedCell] = useState<{
    row: number;
    col: number;
  } | null>(null);

  // Phase 2 S4 — right-click context menu state. null = menu closed.
  // Stores the clicked cell + resolved value + client coords. The resolved
  // value is captured at right-click time (same path as DataCell's render)
  // so CellContextMenu can format it for "Copy value" without re-resolving.
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    cell: GridCell;
    value: unknown;
  } | null>(null);

  // Build the (row,col) → GridCell index ONCE per layout — `findCellAt` would
  // re-scan `layout.cells` per virtual cell otherwise, which is O(N²) on a
  // 100x100 sheet.
  const cellIndex = useMemo(() => buildCellIndex(layout), [layout]);

  // Pre-compute table-input regions for THIS sheet. Re-derived only when
  // the schema's tableInputs[] or the sheet name change (rare). Per-cell
  // edge check then iterates this small array (typically ≤ 5 regions).
  const tableInputRegions = useMemo(
    () => getTableInputRegions(schema.tableInputs, layout.name),
    [schema.tableInputs, layout.name]
  );

  // Section-header rows render as full-width bands (skipping the per-col
  // loop). Build a Set for O(1) per-row check.
  const sectionHeaderRows = useMemo(() => {
    const map = new Map<number, SheetSectionHeader>();
    for (const h of layout.sectionHeaders) map.set(h.row, h);
    return map;
  }, [layout.sectionHeaders]);

  // Increment 2 (model-view-lovable-density-alignment): per-column content-fit
  // width derived from label cells. One O(labelCells) measurement pass per
  // layout change — NOT per render. The offscreen canvas `measureText` is
  // ~µs/call so a full sheet pass (hundreds of label cells) is negligible.
  //
  // Degradation contract: JSDOM has no real canvas `measureText` (returns 0);
  // `measureLabelPx` guards this and returns 0 → clamp to COL_WIDTH.
  // Result: tests stay deterministic (all label cols default to COL_WIDTH).
  //
  // §4 generalization: keyed purely off `cellType === 'label'` + measured text
  // content — zero per-model / per-sheet hardcoding.
  const labelColWidths = useMemo(() => {
    const map = new Map<number, number>();
    for (const cell of layout.cells) {
      if (cell.cellType !== 'label') continue;
      const text = cell.text;
      if (!text) continue;
      const measured = measureLabelPx(text);
      if (measured <= 0) continue;
      // Clamp uses BASE_COL_WIDTH (not the zoom-aware COL_WIDTH) because
      // text measurement is layout-only — independent of zoom. The zoom
      // scaling happens at the virtualizer estimateSize level.
      const clamped = Math.min(
        Math.max(measured, BASE_COL_WIDTH),
        COL_WIDTH_MAX_PX
      );
      const existing = map.get(cell.col);
      if (existing === undefined || clamped > existing) {
        map.set(cell.col, clamped);
      }
    }
    return map;
  }, [layout]);

  // Derive inclusive [startRow, endRow] pixel ranges for each section brick
  // plus the content-column extent used by Increment-1 hugging-brick layout.
  // ADR 0012: fallback chain — (a) trust schema endRow if present, (b) derive
  // from next section start − 1, (c) clamp to last DATA row (layout.rowCount).
  // NOTE: clamp (c) uses layout.rowCount (last data row), NOT layout.rowCount+1
  // (the virtualizer count includes the col-header row at index 0 — the +1 is
  // an implementation detail of rowVirtualizer, not a data boundary).
  // maxPopulatedCol: scan every (r, c) in [startRow,endRow] × [1,colCount] to
  // find the rightmost non-empty column; falls back to layout.colCount so the
  // brick always has a non-zero width on a section with no cells in the index.
  // This memo is NEW and additive; sectionHeaderRows above stays unchanged.
  const sectionRanges = useMemo(() => {
    const sorted = [...layout.sectionHeaders].sort((a, b) => a.row - b.row);
    return sorted.map((h, i) => {
      const startRow = h.row;
      const endRow =
        h.endRow ?? // (a) trust schema field if present
        (sorted[i + 1] ? sorted[i + 1].row - 1 : undefined) ?? // (b) next section start − 1
        layout.rowCount; // (c) runs to last DATA row

      // Content-column extent for hugging-brick (Increment 1).
      // Scan the section's row/col range for the rightmost populated cell.
      let maxPopulatedCol = 0;
      for (let r = startRow; r <= endRow; r++) {
        for (let c = 1; c <= layout.colCount; c++) {
          const cell = cellIndex.get(`${r}-${c}`);
          if (cell && cell.cellType !== 'empty' && c > maxPopulatedCol) {
            maxPopulatedCol = c;
          }
        }
      }
      // Fallback: if no cell found (e.g. sparse section), span all data cols.
      if (maxPopulatedCol === 0) maxPopulatedCol = layout.colCount;

      return { header: h, startRow, endRow, maxPopulatedCol };
    });
  }, [layout.sectionHeaders, layout.rowCount, layout.colCount, cellIndex]);

  const rowVirtualizer = useVirtualizer({
    count: layout.rowCount + 1, // +1 for the col-header row at row=0
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => (index === 0 ? COL_HEADER_HEIGHT : ROW_HEIGHT),
    overscan: 5,
  });

  const colVirtualizer = useVirtualizer({
    horizontal: true,
    count: layout.colCount + 1, // +1 for the row-header col at col=0
    getScrollElement: () => scrollRef.current,
    // Width resolution chain (Increment 2, model-view-lovable-density-alignment):
    //   colWidths?.[i]          — user drag-override wins (Session-3.4 contract)
    //   ?? labelColWidths.get(i) — content-fit measured width for label columns
    //   ?? COL_WIDTH             — default 96px fallback
    //
    // Col 0 is the row-header gutter — always ROW_HEADER_WIDTH, unchanged.
    // The labelColWidths term only changes the *default* when no user override
    // exists, preserving the RotateCcw reset invariant.
    estimateSize: (index) => {
      if (index === 0) return ROW_HEADER_WIDTH;
      return (
        colWidths?.[String(index)] ?? labelColWidths.get(index) ?? COL_WIDTH
      );
    },
    overscan: 3,
  });

  // Re-measure on width-map changes so the virtualizer doesn't render with
  // stale cached sizes after a drag-end. Per-frame drag updates use local
  // visual state in `<ColHeaderCell>` to avoid this round-trip; the
  // re-measure runs only when the parent commits the new width.
  // Also re-measures when labelColWidths changes (new layout loaded) so the
  // virtualizer picks up the content-fit column sizes at initial paint.
  useEffect(() => {
    colVirtualizer.measure();
    // The virtualizer ref is stable across renders; the deps we care about
    // are colWidths (user drag-override), labelColWidths (content-fit,
    // changes with layout), and zoom (DIMENSIONAL scaling — changes the
    // effective COL_WIDTH + ROW_HEADER_WIDTH used in estimateSize).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colWidths, labelColWidths, zoom]);

  // Remeasure the row virtualizer when zoom changes so row heights + the
  // col-header row (estimateSize index 0) pick up the new zoom-aware values.
  // Separate effect from the colVirtualizer re-measure so each has a focused
  // dep list. The virtualizer instance ref is stable (no-op dep omitted).
  useEffect(() => {
    rowVirtualizer.measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  // Tree-navigator scroll-to-cell + pulse. Re-runs on every fresh
  // `scrollToCell` reference (parent bumps a `nonce` per click so re-clicking
  // the same target re-triggers). `align: 'center'` keeps the target away
  // from the freeze edges; `scrollToIndex` is a no-op when the cell is
  // already in the visible window — the pulse still fires.
  useEffect(() => {
    if (!scrollToCell) return;
    rowVirtualizer.scrollToIndex(scrollToCell.row, { align: 'center' });
    colVirtualizer.scrollToIndex(scrollToCell.col, { align: 'center' });
    setPulsedCell({ row: scrollToCell.row, col: scrollToCell.col });
    const timer = setTimeout(() => setPulsedCell(null), 2000);
    return () => clearTimeout(timer);
    // refs are stable; depending on the prop reference is the intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToCell]);

  // ---------------------------------------------------------------------------
  // Phase 2 S3 — Grid-root keyboard handler (ADR-0008).
  //
  // Mount site: `<div role="grid" onKeyDown={handleGridKeyDown}>` at the scroll
  // container root. Handles cell-navigation keys (arrow / Tab / Page / Home /
  // End / Ctrl+Arrow / Escape) + zoom shortcuts (Ctrl+Shift+=/-/0).
  //
  // Three-layer listener design (ADR-0008):
  //   1. Per-cell `onKeyDown` at DataCell (:846-851) — Enter/Space → onClick
  //      (cell activation / CellDetailPanel open). PRESERVED.
  //   2. Grid-root `onKeyDown` here — cell navigation keys. NEW.
  //   3. ModelLayout root `onKeyDown` — fullscreen (F/Esc) + Ctrl+F. UNCHANGED.
  //
  // Closure-staleness: the handler reads `selectedCellRef.current` (a ref kept
  // in sync by the parent via `useEffect [selectedCell]`) rather than closing
  // over the `selectedCell` prop directly. This ensures rapid arrow-key bursts
  // (200+ keypresses/s) always navigate from the LATEST committed cell, not a
  // stale snapshot from when the callback was last re-created.
  // ---------------------------------------------------------------------------

  /** Approximate number of visible rows from the scroll container height. */
  function viewportRowCount(): number {
    const h = scrollRef.current?.clientHeight ?? 600;
    return Math.max(1, Math.floor(h / ROW_HEIGHT));
  }

  /**
   * Excel "Ctrl+Arrow" data-edge jump semantics:
   * - If currently in a non-empty run, jump to the LAST non-empty cell of that run.
   * - If currently in empty, jump to the FIRST non-empty cell encountered.
   * Uses the pre-built `cellIndex` for O(1) lookup per step.
   */
  function findDataEdge(
    current: { row: number; col: number },
    key: string
  ): { row: number; col: number } {
    let { row, col } = current;
    const isEmpty = (r: number, c: number) => {
      const cell = cellIndex.get(`${r}-${c}`);
      return !cell || cell.cellType === 'empty';
    };
    if (key === 'ArrowDown') {
      while (row < layout.rowCount && !isEmpty(row + 1, col)) row++;
      while (row < layout.rowCount && isEmpty(row + 1, col)) row++;
    } else if (key === 'ArrowUp') {
      while (row > 1 && !isEmpty(row - 1, col)) row--;
      while (row > 1 && isEmpty(row - 1, col)) row--;
    } else if (key === 'ArrowRight') {
      while (col < layout.colCount && !isEmpty(row, col + 1)) col++;
      while (col < layout.colCount && isEmpty(row, col + 1)) col++;
    } else if (key === 'ArrowLeft') {
      while (col > 1 && !isEmpty(row, col - 1)) col--;
      while (col > 1 && isEmpty(row, col - 1)) col--;
    }
    return { row, col };
  }

  const handleGridKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      // INPUT/TEXTAREA/contentEditable bail — same shape as ModelLayout's
      // handleKeyDown (research/phase-2.md § 4 block J). Prevents hijacking
      // TreeNavigator search input, InputCellEditor, or any other text field
      // rendered inside the grid area.
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      const current = selectedCellRef?.current ?? null;

      // ========================================================================
      // Phase 3 Session 1 — Ctrl+C / Ctrl+Shift+C (copy value / copy formula).
      // Single-cell only (locked constraint #3 — no multi-select). Shares
      // clipboard path with CellContextMenu (Phase 2) for byte-parity with
      // right-click "Copy value" / "Copy formula". Quiet-fail per
      // CellContextMenu.copyToClipboard precedent. Cross-platform: Ctrl on
      // Win/Linux, Cmd on macOS (e.metaKey).
      // ========================================================================

      // --- Ctrl+Shift+C / Cmd+Shift+C — copy formula (more-specific BEFORE
      // less-specific per Block B ordering invariant). No-op when cell.formula
      // absent (mirrors onCopyFormula:148-151 in CellContextMenu). ---
      if (
        (e.ctrlKey || e.metaKey) &&
        e.shiftKey &&
        (e.key === 'c' || e.key === 'C')
      ) {
        if (!current) return;
        const cell = cellIndex.get(`${current.row}-${current.col}`);
        if (!cell?.formula) return; // no formula — no-op
        e.preventDefault();
        void navigator.clipboard.writeText(cell.formula).catch(() => {
          // Quiet fail — clipboard write blocked (missing permission / insecure
          // context). Matches CellContextMenu/CellDetailPanel/SendDealLinkModal
          // precedent.
        });
        return;
      }

      // --- Ctrl+C / Cmd+C — copy formatted display value (string variant per
      // research § 8 D3 — byte-parity with CellContextMenu.onCopyValue which
      // uses formatCellValue, NOT formatCellValueFragment). ---
      if (
        (e.ctrlKey || e.metaKey) &&
        !e.shiftKey &&
        (e.key === 'c' || e.key === 'C')
      ) {
        if (!current) return;
        const cell = cellIndex.get(`${current.row}-${current.col}`);
        if (!cell) return;
        const cellKey = `${layout.name}!${colToA1(current.col)}${current.row}`;
        const value = cellValues[cellKey];
        const formatted = formatCellValue(value, cell, schema);
        e.preventDefault();
        void navigator.clipboard.writeText(formatted).catch(() => {
          // Quiet fail — same rationale.
        });
        return;
      }

      // Zoom shortcuts — Ctrl+Shift+=/-/0 (S2 dependency).
      // These are also handled on ModelLayout root, but intercepting here lets
      // us preventDefault early when focus is inside the grid. `onZoomChange`
      // is optional; fall through to bubble-up if not wired.
      if (e.ctrlKey && e.shiftKey && (e.key === '=' || e.key === '+')) {
        if (!onZoomChange) return;
        e.preventDefault();
        const idx = ZOOM_LEVELS.indexOf(zoom);
        if (idx < ZOOM_LEVELS.length - 1) onZoomChange(ZOOM_LEVELS[idx + 1]);
        return;
      }
      if (e.ctrlKey && e.shiftKey && e.key === '-') {
        if (!onZoomChange) return;
        e.preventDefault();
        const idx = ZOOM_LEVELS.indexOf(zoom);
        if (idx > 0) onZoomChange(ZOOM_LEVELS[idx - 1]);
        return;
      }
      if (e.ctrlKey && e.shiftKey && e.key === '0') {
        if (!onZoomChange) return;
        e.preventDefault();
        onZoomChange(DEFAULT_ZOOM);
        return;
      }

      // Escape — clear selection.
      if (e.key === 'Escape') {
        if (!current) return; // no selection — let parent handle (fullscreen Esc)
        e.preventDefault();
        onClearSelection?.();
        return;
      }

      // All remaining nav keys require an active selection.
      if (!current) {
        // Ctrl+Home without a selection: jump to A1.
        if (e.ctrlKey && e.key === 'Home') {
          e.preventDefault();
          onCellSelect(1, 1);
          rowVirtualizer.scrollToIndex(1, { align: 'auto' });
          colVirtualizer.scrollToIndex(1, { align: 'auto' });
        }
        return;
      }

      const { row, col } = current;
      let nextRow = row;
      let nextCol = col;
      let handled = true;

      switch (e.key) {
        case 'ArrowUp':
          nextRow = Math.max(1, row - 1);
          break;
        case 'ArrowDown':
          nextRow = Math.min(layout.rowCount, row + 1);
          break;
        case 'ArrowLeft':
          nextCol = Math.max(1, col - 1);
          break;
        case 'ArrowRight':
          nextCol = Math.min(layout.colCount, col + 1);
          break;

        case 'Tab':
          if (e.shiftKey) {
            if (col <= 1) {
              nextRow = Math.max(1, row - 1);
              nextCol = layout.colCount;
            } else {
              nextCol = col - 1;
            }
          } else {
            if (col >= layout.colCount) {
              nextRow = Math.min(layout.rowCount, row + 1);
              nextCol = 1;
            } else {
              nextCol = col + 1;
            }
          }
          break;

        // Enter / Shift+Enter — next/prev row, same column.
        // NOTE: Per-cell DataCell onKeyDown catches Enter/Space when a DATA CELL
        // is focused (roving-tabindex at DataCell:957) and invokes onClick. The
        // grid-root handler only sees Enter when focus is on the grid container
        // itself, not a focused child cell. Both handlers coexist cleanly.
        case 'Enter':
          if (e.shiftKey) {
            nextRow = Math.max(1, row - 1);
          } else {
            nextRow = Math.min(layout.rowCount, row + 1);
          }
          break;

        case 'PageDown':
          nextRow = Math.min(layout.rowCount, row + viewportRowCount());
          break;
        case 'PageUp':
          nextRow = Math.max(1, row - viewportRowCount());
          break;

        case 'Home':
          if (e.ctrlKey) {
            nextRow = 1;
            nextCol = 1;
          } else {
            nextCol = 1;
          }
          break;
        case 'End':
          if (e.ctrlKey) {
            nextRow = layout.rowCount;
            nextCol = layout.colCount;
          } else {
            nextCol = layout.colCount;
          }
          break;

        default:
          // Ctrl+Arrow — data-edge jump.
          if (e.ctrlKey && e.key.startsWith('Arrow')) {
            const edge = findDataEdge({ row, col }, e.key);
            nextRow = edge.row;
            nextCol = edge.col;
          } else {
            handled = false;
          }
          break;
      }

      if (handled) {
        e.preventDefault();
        onCellSelect(nextRow, nextCol);
        // Auto-scroll past viewport edges — 'auto' is a no-op when the target
        // is already in the visible window (research/phase-2.md § 4 block M + D5).
        rowVirtualizer.scrollToIndex(nextRow, { align: 'auto' });
        colVirtualizer.scrollToIndex(nextCol, { align: 'auto' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedCellRef,
      onCellSelect,
      onClearSelection,
      onZoomChange,
      zoom,
      layout.rowCount,
      layout.colCount,
      layout.name,
      cellIndex,
      cellValues,
      schema,
      // rowVirtualizer + colVirtualizer are stable refs (virtualizer doesn't
      // re-create on re-render); omitting from deps is intentional.
    ]
  );

  // Phase 2 S4 — per-cell right-click handler. Prevents the browser's native
  // context menu and opens <CellContextMenu> at the cursor's client coords.
  // `value` is the already-resolved display value from the DataCell render path
  // (resolveValue result) — captured here so CellContextMenu can format it for
  // "Copy value" without accessing cellValues/inputValues directly.
  const onCellContextMenu = useCallback(
    (e: MouseEvent, cell: GridCell, value: unknown) => {
      e.preventDefault();
      setContextMenu({ x: e.clientX, y: e.clientY, cell, value });
    },
    []
  );

  const rowItems = rowVirtualizer.getVirtualItems();
  const colItems = colVirtualizer.getVirtualItems();
  const totalHeight = rowVirtualizer.getTotalSize();
  const totalWidth = colVirtualizer.getTotalSize();

  // Pre-filter row/col virtual items into header vs body slices once per
  // render. Header items (index === 0) drive the sticky strips; body items
  // (index > 0) drive the data + section-header pass. Filtering O(N) over
  // virtual items only — already a small list (visible window + overscan).
  const bodyRowItems = rowItems.filter((r) => r.index > 0);
  const bodyColItems = colItems.filter((c) => c.index > 0);

  // Inc-4c dispatch — panel-dashboard grammar (model-view-lovable-density-alignment).
  // Early-return after ALL hooks so Rules of Hooks is respected. G2 columnar-brief
  // and G3 grid paths fall through to the existing virtualized render tree (unchanged).
  // Pre-feature layouts (composition undefined) also fall through to G3 (ADR 0014).
  if (
    layout.composition?.grammar === 'panel-dashboard' &&
    layout.composition.panels?.length
  ) {
    return (
      <PanelDashboard
        layout={layout}
        cellValues={cellValues}
        inputValues={inputValues}
        schema={schema}
        selectedCell={selectedCell}
        onCellSelect={onCellSelect}
        readOnly={readOnly}
        zoom={zoom}
        scrollToCell={scrollToCell}
      />
    );
  }

  return (
    <>
      <div
        ref={scrollRef}
        className="flex-1 overflow-auto bg-bg relative"
        data-testid="model-grid"
        role="grid"
        aria-rowcount={layout.rowCount}
        aria-colcount={layout.colCount}
        aria-label={`Model grid for sheet ${layout.name}`}
        tabIndex={-1}
        onKeyDown={handleGridKeyDown}
      >
        <div
          style={{
            height: totalHeight,
            width: totalWidth,
            position: 'relative',
          }}
        >
          {/* Layer 1 — Data cells + section headers. Absolute-positioned at
            virtualRow/Col offsets within the inner totalSize div. */}
          {bodyRowItems.map((rowItem) => {
            const row = rowItem.index;
            const headerForRow = sectionHeaderRows.get(row);
            if (headerForRow) {
              const contentLeft = ROW_HEADER_WIDTH;
              // F6 (model-view-excel-parity Phase 1, ADR-0013, D13 re-framed mechanic):
              // SectionHeaderRow banner spans content extent (NOT a single section's
              // maxPopulatedCol — too narrow on MCF where each section has its own
              // populated range — NOR layout.colCount allocation extent, which on
              // sparse sheets (RenoAssumption, AnnualWaterfall) overshoots into
              // dead-space. F-I3 post-ship remediation: use the SHEET-wide content
              // extent = max maxPopulatedCol across all section ranges. Bricks
              // continue to hug their per-section content via brickWidth.
              const sheetMaxPopulatedCol = sectionRanges.reduce(
                (m, r) => Math.max(m, r.maxPopulatedCol),
                1
              );
              const contentWidth = sumColWidths(
                1,
                sheetMaxPopulatedCol,
                colWidths,
                labelColWidths,
                COL_WIDTH
              );
              return (
                <SectionHeaderRow
                  key={`sh-${row}`}
                  header={headerForRow}
                  virtualRow={rowItem}
                  contentLeft={contentLeft}
                  contentWidth={contentWidth}
                  zoomFontSize={FONT_SIZE}
                  zoomRowHeight={ROW_HEIGHT}
                />
              );
            }
            return bodyColItems.map((colItem) => {
              const col = colItem.index;
              const cell = cellIndex.get(`${row}-${col}`);
              if (!cell || cell.cellType === 'empty') {
                return null;
              }
              if (cell.mergedInto) return null;

              const value = resolveValue(
                cell,
                cellValues,
                inputValues,
                layout.name
              );
              const isSelected =
                selectedCell !== null &&
                selectedCell.row === row &&
                selectedCell.col === col;
              const isPulsing =
                pulsedCell !== null &&
                pulsedCell.row === row &&
                pulsedCell.col === col;
              const inFreeze =
                row <= layout.frozenRows || col <= layout.frozenCols;

              return (
                <DataCell
                  key={`${row}-${col}`}
                  cell={cell}
                  value={value}
                  schema={schema}
                  virtualRow={rowItem}
                  virtualCol={colItem}
                  selected={isSelected}
                  pulsing={isPulsing}
                  inFreeze={inFreeze}
                  tableRegions={tableInputRegions}
                  sheetName={layout.name}
                  colWidths={colWidths}
                  labelColWidths={labelColWidths}
                  zoomFontSize={FONT_SIZE}
                  zoomRowHeight={ROW_HEIGHT}
                  zoomColWidth={COL_WIDTH}
                  onClick={() => onCellSelect(row, col)}
                  onContextMenu={(e) => onCellContextMenu(e, cell, value)}
                />
              );
            });
          })}

          {/* Section brick frames — one zIndex-0 absolute border per section,
            co-planar with (not wrapping) the data cells. ADR 0013 (overlay
            frame) + ADR 0012 (range derivation) + ADR 0014 (offset technique).
            Offset technique = spike-confirmed `index * ROW_HEIGHT`, ADR 0014;
            grid is fixed-row-height (estimateSize uniform 22px, no measureElement). */}
          {sectionRanges.map(
            ({ header, startRow, endRow, maxPopulatedCol }) => {
              const top = startRow * ROW_HEIGHT; // index * ROW_HEIGHT — valid ∀ index (fixed-size virtualizer)
              const bottom = endRow * ROW_HEIGHT;
              const height = bottom - top + ROW_HEIGHT; // = (endRow − startRow + 1) * ROW_HEIGHT, inclusive of endRow row
              // Increment-1 hugging-brick: left = first data column x-offset
              // (ROW_HEADER_WIDTH, same basis as virtualCol.start for col index 1).
              // width = sum of populated data-column widths (cols 1..maxPopulatedCol).
              // This excludes the row-gutter (left: 0 → ROW_HEADER_WIDTH) and the
              // dead-space to the right (totalWidth → content extent only).
              const brickLeft = ROW_HEADER_WIDTH;
              // Pass zoom-aware COL_WIDTH as the default so brick widths scale
              // lockstep with the virtualizer's estimateSize (ADR-0012 single-source).
              const brickWidth = sumColWidths(
                1,
                maxPopulatedCol,
                colWidths,
                labelColWidths,
                COL_WIDTH
              );
              return (
                <div
                  key={`section-brick-${header.row}`}
                  data-testid={`section-brick-${header.row}`}
                  // INC-1 FIX 2026-05-19: bg-panel-bg removed — Lovable's DevBudgetBrief.tsx:258 is
                  // border-only (panel-border-hi hex), NO fill. The opaque fill was painting over cells
                  // in section ranges (bricks emit AFTER cells in DOM order at same zIndex:0).
                  // Selection-ring z-[1] in cell-style.ts:321-323 was the only rescue; resting-state
                  // was broken on all G3 sheets across both models.
                  // F1 (model-view-excel-parity Phase 1, ADR-0013): 1px → 2px single-stroke for stronger
                  // Excel-parity section frame on dark theme. Geometry UNTOUCHED — ADR-0014 D-OFFSET
                  // invariant preserved (top = startRow * ROW_HEIGHT, offset is index*ROW_HEIGHT).
                  className="border-2 border-panel-border-hi pointer-events-none rounded-[3px]"
                  style={{
                    position: 'absolute',
                    top,
                    left: brickLeft,
                    width: brickWidth,
                    height,
                    zIndex: 0,
                  }}
                />
              );
            }
          )}

          {/* Phase 2 S3 NEW — Row-highlight stripe (research/phase-2.md § 11 R-H).
            Absolute-positioned overlay at the selected row's y-position.
            zIndex: 0 — below the selection ring (z-[1] in cell-style.ts:336)
            but above the same-z-index data cells (DOM order: stripe is emitted
            AFTER section bricks and data cells, so it paints last at z0).
            pointer-events-none: clicks fall through to the data cells beneath.
            ADR-0014 D-OFFSET: top = selectedCell.row * ROW_HEIGHT (identical
            to the section-brick offset technique, spike-confirmed fixed-height).
            width = totalWidth so the stripe spans the full sheet width including
            the row-header gutter. */}
          {selectedCell && (
            <div
              data-testid="row-highlight-stripe"
              className="bg-accent/15 pointer-events-none"
              style={{
                position: 'absolute',
                top: selectedCell.row * ROW_HEIGHT,
                left: 0,
                width: totalWidth,
                height: ROW_HEIGHT,
                zIndex: 0,
              }}
              aria-hidden="true"
            />
          )}

          {/* Layer 2 — Col-header strip. Rendered FIRST in flow so its
            natural flow position is at the top of the inner div; `position:
            sticky; top: 0` pins it to the scroll container's top during
            vertical scroll. NO `left` set — the strip scrolls horizontally
            with content (col headers track horizontal scroll). Children at
            `left: virtualCol.start` are absolute-positioned within the
            strip; they move with the strip during horizontal scroll.
            Flow contribution: COL_HEADER_HEIGHT, which is the row-0 slot
            of totalHeight (rowVirtualizer index 0 has that exact size). */}
          <div
            data-testid="col-header-strip"
            style={{
              position: 'sticky',
              top: 0,
              width: totalWidth,
              height: COL_HEADER_HEIGHT,
              zIndex: 2,
            }}
          >
            {/* Corner cell rendered as a child of the col-header strip so its
              natural flow position is at the strip's top-left (the strip's
              flow y=0). With `position: sticky; left: 0; zIndex: 3` it pins
              horizontally against the scroll container while moving with
              the strip vertically (which itself pins at top: 0). The
              combined effect: corner is always visible at the viewport's
              top-left. Nested sticky works because each axis resolves
              against the nearest scrolling ancestor (the scroll container),
              not the parent. */}
            <CornerCell
              rowHeaderWidth={ROW_HEADER_WIDTH}
              colHeaderHeight={COL_HEADER_HEIGHT}
            />
            {bodyColItems.map((colItem) => (
              <ColHeaderCell
                key={`ch-${colItem.index}`}
                col={colItem.index}
                virtualCol={colItem}
                onResize={onColWidthChange}
                colHeaderHeight={COL_HEADER_HEIGHT}
                isHighlight={
                  selectedCell !== null && selectedCell.col === colItem.index
                }
              />
            ))}
          </div>

          {/* Layer 3 — Row-header column. `position: sticky; left: 0` pins to
            the scroll container's left during horizontal scroll. NO `top`
            set — the column scrolls vertically with content (row 1's
            row-header tracks row 1's data row through vertical scroll).
            Rendered AFTER the col-header strip so its flow position starts
            at y=COL_HEADER_HEIGHT, fitting cleanly under the strip.
            Height = totalHeight - COL_HEADER_HEIGHT (the data area's
            vertical extent). Children at `top: virtualRow.start` are
            absolute-positioned within the column; they move with the
            column during vertical scroll. */}
          <div
            data-testid="row-header-column"
            style={{
              position: 'sticky',
              left: 0,
              width: ROW_HEADER_WIDTH,
              height: totalHeight - COL_HEADER_HEIGHT,
              zIndex: 2,
            }}
          >
            {bodyRowItems
              .filter((r) => !sectionHeaderRows.has(r.index))
              .map((rowItem) => (
                <RowHeaderCell
                  key={`rh-${rowItem.index}`}
                  row={rowItem.index}
                  virtualRow={rowItem}
                  rowHeaderWidth={ROW_HEADER_WIDTH}
                  colHeaderHeight={COL_HEADER_HEIGHT}
                  isHighlight={
                    selectedCell !== null && selectedCell.row === rowItem.index
                  }
                />
              ))}
          </div>

          {/* (Layer 4 — Corner cell — moved into the col-header strip above.
            See note inside the strip for rationale.) */}
        </div>
      </div>
      {/* Phase 2 S4 — context menu portal. Rendered as a sibling of the scroll
        container (NOT inside it) so it escapes overflow-hidden clipping.
        Portal to document.body via <CellContextMenu> so z-9999 is unambiguous.
        Null-check: contextMenu === null → nothing rendered. */}
      {contextMenu && (
        <CellContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          cell={contextMenu.cell}
          value={contextMenu.value}
          sheetName={layout.name}
          schema={schema}
          conversationId={conversationId}
          onReveal={onReveal ?? (() => undefined)}
          onShowProvenance={onShowProvenance ?? (() => undefined)}
          onViewInConversation={
            conversationId !== null ? onViewInConversation : undefined
          }
          onClose={() => setContextMenu(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Cell components
// ---------------------------------------------------------------------------

function CornerCell({
  rowHeaderWidth,
  colHeaderHeight,
}: {
  rowHeaderWidth: number;
  colHeaderHeight: number;
}) {
  return (
    <div
      // F8 (model-view-excel-parity Phase 1, ADR-0013): border-b → border-b-2
      // for visual coherence with ColHeaderCell and RowHeaderCell sibling bump.
      className="bg-panel-bg border-r border-b-2 border-panel-border-hi"
      style={{
        position: 'sticky',
        top: 0,
        left: 0,
        width: rowHeaderWidth,
        height: colHeaderHeight,
        zIndex: 3,
      }}
    />
  );
}

function ColHeaderCell({
  col,
  virtualCol,
  onResize,
  colHeaderHeight,
  isHighlight,
}: {
  col: number;
  virtualCol: VirtualItem;
  onResize?: (colIndex: number, widthPx: number) => void;
  colHeaderHeight: number;
  /** Phase 2 S3 — true when this column is the selected cell's column. */
  isHighlight?: boolean;
}) {
  // Renders inside the sticky col-header strip (Layer 3). The strip pins to
  // the scroll container's top; this cell uses absolute positioning at
  // `left: virtualCol.start` to slot into the virtualized horizontal axis.
  //
  // Session 3.4 (model-view-ux-polish, 2026-04-27): the cell now hosts a
  // 4px drag handle on its right edge. During the drag, local visual state
  // (`dragWidth`) overrides `virtualCol.size` so the user sees the resize
  // mid-stream without re-measuring the virtualizer per frame. On
  // pointerup, the clamped final width fires `onResize(col, widthPx)` —
  // the parent persists via the debounced PATCH and re-passes the new
  // `colWidths` map; the virtualizer's `useEffect → measure()` then
  // re-computes once. The `data-resizing` attribute is exposed for tests +
  // potential CSS hooks.
  const startXRef = useRef<number | null>(null);
  const startWidthRef = useRef<number>(virtualCol.size);
  const [dragWidth, setDragWidth] = useState<number | null>(null);

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLDivElement).setPointerCapture(e.pointerId);
    startXRef.current = e.clientX;
    startWidthRef.current = virtualCol.size;
    setDragWidth(virtualCol.size);
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (startXRef.current == null) return;
    const delta = e.clientX - startXRef.current;
    setDragWidth(clampWidth(startWidthRef.current + delta));
  };

  const handlePointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (startXRef.current == null) return;
    (e.target as HTMLDivElement).releasePointerCapture(e.pointerId);
    const final = dragWidth ?? virtualCol.size;
    startXRef.current = null;
    setDragWidth(null);
    if (onResize) onResize(col, final);
  };

  const visualWidth = dragWidth ?? virtualCol.size;

  return (
    <div
      // F8 (model-view-excel-parity Phase 1, ADR-0013): border-b → border-b-2 + font-bold.
      // border-b-2: stronger separator matching Excel's column-header rule weight.
      // font-bold: column labels (A, B, C…) get bold weight for visual scan; row
      // indices (RowHeaderCell) do NOT — per S5 step 5.3 decision.
      // Phase 2 S3: isHighlight applies bg-accent/15 + text-text-bright when this
      // col matches the selected cell's column (static Tailwind v4 literals — no interpolation).
      className={clsx(
        'flex items-center justify-center border-r border-b-2 border-panel-border-hi text-[10px] font-mono font-bold tabular-nums select-none',
        isHighlight
          ? 'bg-accent/15 text-text-bright'
          : 'bg-panel-bg text-text-muted'
      )}
      style={{
        position: 'absolute',
        top: 0,
        left: virtualCol.start,
        width: visualWidth,
        height: colHeaderHeight,
      }}
      data-testid={`col-header-${col}`}
      data-resizing={dragWidth !== null ? 'true' : undefined}
    >
      {colToA1(col)}
      {/* Drag handle — 4px hit zone on the right edge, transparent until
          hover. `cursor-col-resize` signals the affordance.
          `e.stopPropagation()` in pointerdown prevents the parent header
          cell's click from selecting / scrolling. */}
      <div
        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-text-dim"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        data-testid={`col-resize-handle-${col}`}
      />
    </div>
  );
}

function RowHeaderCell({
  row,
  virtualRow,
  rowHeaderWidth,
  colHeaderHeight,
  isHighlight,
}: {
  row: number;
  virtualRow: VirtualItem;
  rowHeaderWidth: number;
  colHeaderHeight: number;
  /** Phase 2 S3 — true when this row is the selected cell's row. */
  isHighlight?: boolean;
}) {
  // Renders inside the sticky row-header column wrapper (Layer 3). The
  // wrapper starts at flow y=colHeaderHeight (after the col-header strip)
  // and spans (totalHeight - colHeaderHeight). Children's `top` is
  // relative to the wrapper, so subtract colHeaderHeight from
  // virtualRow.start (which is in inner-div coords, where row 0 = 0,
  // row 1 = colHeaderHeight, row 2 = colHeaderHeight + rowHeight, ...).
  return (
    <div
      // F8 (model-view-excel-parity Phase 1, ADR-0013): border-b → border-b-2
      // for visual coherence with ColHeaderCell sibling bump. No font-bold —
      // row indices (numeric) don't need bolding per S5 step 5.3 decision.
      // Phase 2 S3: isHighlight applies bg-accent/15 + text-text-bright when
      // this row matches the selected cell's row (static Tailwind v4 literals).
      className={clsx(
        'flex items-center justify-center border-r border-b-2 border-panel-border-hi text-[10px] font-mono tabular-nums select-none',
        isHighlight
          ? 'bg-accent/15 text-text-bright'
          : 'bg-panel-bg text-text-muted'
      )}
      style={{
        position: 'absolute',
        top: virtualRow.start - colHeaderHeight,
        left: 0,
        width: rowHeaderWidth,
        height: virtualRow.size,
      }}
      data-testid={`row-header-${row}`}
    >
      {row}
    </div>
  );
}

interface DataCellProps {
  cell: GridCell;
  value: unknown;
  schema: ModelViewSchemaSubset;
  virtualRow: VirtualItem;
  virtualCol: VirtualItem;
  selected: boolean;
  pulsing: boolean;
  inFreeze: boolean;
  tableRegions: TableInputRegion[];
  /** Sheet name — threaded into `getCellClassName` so the unmapped-fillColor
   *  console.warn key has context. Added by model-view-fidelity Phase 2. */
  sheetName: string;
  /** Per-column width overrides — read by the merge-anchor span math so a
   *  merged region renders with the actual summed widths of its spanned
   *  columns (Session 3.4, model-view-ux-polish). */
  colWidths?: Record<string, number>;
  /** Content-fit label column widths (Increment 2, model-view-lovable-density-
   *  alignment). Used in the merge-anchor span math to maintain lockstep with
   *  the colVirtualizer's estimateSize resolution chain. */
  labelColWidths?: Map<number, number>;
  /**
   * Zoom-aware font size in px — derived from `Math.round(BASE_FONT_SIZE * zoomFactor)`
   * in the parent ModelGrid component (single-source, ADR-0012). Passed via prop
   * because DataCell is a module-scope component (not a hook) that cannot read
   * component-scope constants. Tailwind v4 no-safelist constraint means
   * `text-[${n}px]` class interpolation is forbidden; inline style is the
   * escape valve per spec Locked Constraints + apps/web CLAUDE.md.
   */
  zoomFontSize: number;
  /**
   * Zoom-aware row height in px — used for `lineHeight` in inline style so
   * single-line cell text is vertically centred at every zoom level without
   * `display: flex` (which would break the column-aligned `$` layout path).
   * Also used for merge-anchor row span height math (ADR-0012 single-source).
   */
  zoomRowHeight: number;
  /**
   * Zoom-aware default column width in px — used as the fallback in the
   * merge-anchor span width calculation so merged regions scale lockstep
   * with the virtualizer's estimateSize (ADR-0012 single-source).
   */
  zoomColWidth: number;
  onClick: () => void;
  /** Phase 2 S4 — right-click handler. Passed from ModelGrid's onCellContextMenu. */
  onContextMenu?: (e: MouseEvent) => void;
}

/**
 * Per-cell renderer with full Session 3 styling:
 *   - Cell-type tokens (label / input / formula+output / empty) via
 *     `getCellClassName`
 *   - Value formatting via `formatCellValueFragment` (F4, ADR-0001: Excel
 *     numberFormat → dataType → Intl fallback; currency cells decompose into
 *     prefix/body for flexbox `justify-between` column-aligned `$`)
 *   - Error-literal override (`text-danger` regardless of cell type) when
 *     `isErrorValue(value)` matches one of the 8 known patterns
 *   - Table-input bracket on the leftmost col of any tableInput region
 *   - Selection ring via `outline outline-2 outline-accent`
 *   - Merge-anchor cells render at `cols × COL_WIDTH × rows × ROW_HEIGHT`
 *     with `zIndex: 1` so they overlay non-anchor merged cells (which
 *     return `null` upstream)
 */
function DataCell({
  cell,
  value,
  schema,
  virtualRow,
  virtualCol,
  selected,
  pulsing,
  inFreeze,
  tableRegions,
  sheetName,
  colWidths,
  labelColWidths,
  zoomFontSize,
  zoomRowHeight,
  zoomColWidth,
  onClick,
  onContextMenu,
}: DataCellProps) {
  // Merge-anchor span math (Session 3.4 + Increment 2 lockstep extension):
  // the anchor must sum the spanned columns' actual widths using the SAME
  // resolution chain as colVirtualizer.estimateSize so merged regions span
  // the correct pixel width regardless of content-fit or user overrides.
  // Resolution chain: colWidths?.[i] ?? labelColWidths?.get(i) ?? zoomColWidth.
  // Non-merged cells use virtualCol.size (already resolved by estimateSize).
  const width = cell.mergeSpan
    ? sumColWidths(
        cell.col,
        cell.mergeSpan.cols,
        colWidths,
        labelColWidths,
        zoomColWidth
      )
    : virtualCol.size;
  // Merge-anchor height uses zoomRowHeight (passed from ModelGrid's component-
  // scope zoom-aware constant) so the merged region spans the correct px height
  // at every zoom level (ADR-0012 single-source, zoom-aware).
  const height = cell.mergeSpan
    ? cell.mergeSpan.rows * zoomRowHeight
    : virtualRow.size;

  const isMissing = isMissingValueError(value);
  // `#N/A` is captured by `isMissing`; `isError` covers the remaining 8
  // error literals (#REF!/#VALUE!/#NUM!/#CYCLE!/#NAME?/#NULL!/#DIV/0!/#ERROR!).
  // Mutually exclusive — `isError` ignores #N/A so the missing-value path
  // owns rendering for that single literal.
  const isError = !isMissing && isErrorValue(value);
  const bracketLeft = isLeftEdgeOfTableRegion(cell.row, cell.col, tableRegions);
  // F4 (model-view-excel-parity Phase 1, ADR-0001): always call fragment;
  // currency/currency_per_sf/currency_per_unit_year cells get flexbox
  // justify-between for column-aligned `$` glyphs. All other cells degrade
  // to {body} with no prefix/suffix — back-compat with prior string render.
  // `formatCellValue` (string variant) is preserved for non-DataCell consumers
  // (CellDetailPanel, PanelDashboard) per ADR-0001.
  const fragment = formatCellValueFragment(value, cell, schema);
  const hasColumnAlignment = fragment.prefix !== undefined;
  // Excel-parity (2026-05-25): workbook convention `#,##0;[Red](#,##0)` —
  // negative numbers rendered in parens AND red. Detect from the formatted
  // fragment (covers both Accounting numberFormat path AND schema-dataType
  // path; both emit `($...)` for negatives via `formatExcelNumber` /
  // `splitCurrencyParts` respectively).
  const isNegativeAccounting =
    typeof value === 'number' &&
    value < 0 &&
    (fragment.body.startsWith('(') || fragment.prefix === '(');

  return (
    <div
      role="gridcell"
      aria-rowindex={cell.row + 1}
      aria-colindex={cell.col + 1}
      aria-selected={selected ? 'true' : undefined}
      id={`cell-${sheetName}-${cell.row}-${cell.col}`}
      tabIndex={selected ? 0 : -1}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      data-testid={`cell-${cell.row}-${cell.col}`}
      data-cell-type={cell.cellType}
      data-merge-anchor={cell.mergeSpan ? 'true' : undefined}
      data-error={isError ? 'true' : undefined}
      data-missing={isMissing ? 'true' : undefined}
      data-bracket-left={bracketLeft ? 'true' : undefined}
      data-pulsing={pulsing ? 'true' : undefined}
      data-empty={cell.cellType === 'empty' ? '' : undefined}
      title={getCellErrorAddress(value)}
      className={getCellClassName(cell, selected, {
        isError,
        isMissing,
        bracketLeft,
        pulsing,
        sheet: sheetName,
        isNegativeAccounting,
      })}
      style={{
        position: 'absolute',
        top: virtualRow.start,
        left: virtualCol.start,
        width,
        height,
        // Phase 2 S2 DIMENSIONAL zoom — font-size + line-height via inline
        // style (Tailwind v4 no-safelist: class interpolation is forbidden;
        // inline style is the escape valve per ADR-0012 + spec Locked
        // Constraints). Tailwind's text-[11px] leading-[22px] base classes
        // remain in getCellClassName for the z=100 reference and are visually
        // overridden by these inline declarations at other zoom levels.
        fontSize: `${zoomFontSize}px`,
        lineHeight: `${zoomRowHeight}px`,
        // Excel-parity (2026-05-25): negative-in-parens render in red,
        // matching workbook's `#,##0;[Red](#,##0)` convention. Inline color
        // (NOT a Tailwind class) because Tailwind v4 cascade is stylesheet-
        // rule-order driven and `.text-text` lands after `.text-danger` in
        // the compiled CSS for formula/output cells — class-based override
        // loses; inline always wins.
        ...(isNegativeAccounting ? { color: 'var(--color-danger)' } : null),
        // Freeze-pane sticky: raise z-index so frozen cells overlay scrolling
        // ones. Merge anchors also get z-index:1 to cover their non-anchor
        // siblings; freeze + merge stacks at z-index:2.
        ...(inFreeze ? { zIndex: cell.mergeSpan ? 2 : 1 } : null),
        ...(!inFreeze && cell.mergeSpan ? { zIndex: 1 } : null),
      }}
    >
      {hasColumnAlignment ? (
        <div className="flex justify-between w-full">
          <span>{fragment.prefix}</span>
          <span>
            {fragment.body}
            {fragment.suffix}
          </span>
        </div>
      ) : fragment.placeholder ? (
        // 2026-05-27 — empty `dataType: 'location'` input cells (and any
        // future placeholder-bearing fragments) render the body in a muted
        // italic CTA style so the user sees an affordance instead of a
        // visually-tiny empty box.
        <span className="text-text-muted italic">{fragment.body}</span>
      ) : (
        <>
          {fragment.body}
          {fragment.suffix}
        </>
      )}
    </div>
  );
}

/**
 * Full-row band rendered in place of the per-column loop for rows that
 * appear in `layout.sectionHeaders`. The band spans the section's data
 * columns only (Increment-1 hugging-brick: left = ROW_HEADER_WIDTH, width =
 * content-column extent) so the grey opener band is flush atop and exactly
 * as wide as the co-planar brick frame below it. Uses the `bg-band-section`
 * patina opener band + `text-text-bright font-bold`
 * (model-view-lovable-density-alignment Phase 4 Session 3; hugging updated
 * Increment 1).
 */
function SectionHeaderRow({
  header,
  virtualRow,
  contentLeft,
  contentWidth,
  zoomFontSize,
  zoomRowHeight,
}: {
  header: SheetSectionHeader;
  virtualRow: VirtualItem;
  /** Left x-offset of the first data column (= ROW_HEADER_WIDTH). */
  contentLeft: number;
  /** Sum of populated data-column widths for this section. */
  contentWidth: number;
  /** Excel-parity (2026-05-25): section header bands match data-row height
   *  and font scale (Excel's inline section headers sit in the same row
   *  rhythm as data; treat them as bold-tinted data rows, NOT giant strips).
   *  Pre-fix: `text-xs` (12px) + `px-3` were hardcoded; at 50%/75% zoom
   *  text overflowed the zoom-shrunk row container, making the banner
   *  visually 2-3x taller than data rows. */
  zoomFontSize: number;
  zoomRowHeight: number;
}) {
  const padX = Math.max(4, Math.round(8 * (zoomRowHeight / 22)));
  return (
    <div
      role="row"
      data-testid={`section-header-${header.row}`}
      className="flex items-center bg-band-section text-text-bright font-mono font-bold tracking-[0.5px] border-b border-panel-border overflow-hidden"
      style={{
        position: 'absolute',
        top: virtualRow.start,
        left: contentLeft,
        width: contentWidth,
        height: virtualRow.size,
        fontSize: `${zoomFontSize}px`,
        lineHeight: `${zoomRowHeight}px`,
        paddingLeft: `${padX}px`,
        paddingRight: `${padX}px`,
        zIndex: 1,
      }}
    >
      {header.label}
    </div>
  );
}
